import * as THREE from "three"

export const createViewerSettingsController = ({
  state,
  element,
  renderer,
  camera,
  controls,
  grid,
  referenceLayerCreateForm,
  referenceLayerCreateNameInput,
  referenceLayerCreateFileInput,
  findReferenceLayerById,
  initialReferenceLayerId,
  cloneCablingPlanSettings,
  cloneViewerSettings,
  viewerSettingsFromPayload,
  activeReferenceLayer,
  anyViewerSettingsDirty,
  syncActiveReferenceLayerVisibilityState,
  renderReferenceLayerMenu,
  renderReferenceLayerList,
  setActiveButton,
  renderInspector,
  syncReferenceLayersDataset,
  syncViewerDataset,
  applyViewerLabelsVisibilityPreference,
  applyModelOffsetToScene,
  refreshModelFraming,
  applyReferenceLayerSettingsToOverlay,
  rebuildPlacementObjects,
  rebuildNavigationPointObjects,
  rebuildCaptureAlignmentObjects,
  refreshSelectionHelper,
  refreshHoverHelper,
  apply2DView,
  applyOrthographic2DView,
  applyWalkthroughView,
  apply3DView,
  defaultViewVerticalOffsetDistance,
  deriveWalkthroughHelperSettings,
  walkthroughFocusPoint,
  syncActiveReferenceLayerDraftToCollection,
  markReferenceLayerSaved,
  savedReferenceLayerDraft,
  referenceLayerDirtyFor,
  ensureCablingPlanOverlay,
  ensureVisibleReferenceLayerOverlays,
  syncCablingPlanOverlayPresentation,
  disposeCablingPlanOverlay,
  scheduleCablingPlanTextureRefresh,
  applyCablingPlanSettingsToOverlay,
  closePopup,
  clearHoveredObject,
  clearSelectedObject,
  expandViewerSettingsForCurrentView,
  setFeedback,
  apiRequest,
  syncCablingPlanForm,
  notifyEmbedStateChanged,
  syncNavigationPointVisibility,
  parseBooleanOr,
  is2DLikeMode,
  isOrthographic2DMode,
  isPerspective2DMode,
  clampNumber,
  parseFloatOr
}) => {
  const referenceLayerPayload = (referenceLayer = activeReferenceLayer()) => {
    const settings = cloneCablingPlanSettings(referenceLayer?.settings || {})

    return {
      name: referenceLayer?.name?.toString().trim() || "",
      default_visible: referenceLayer?.defaultVisible ?? false,
      offset_x: settings.offsetX,
      offset_z: settings.offsetZ,
      elevation: settings.elevation,
      rotation_degrees: settings.rotationDegrees,
      scale: settings.scale,
      opacity: settings.opacity,
      white_threshold: settings.whiteThreshold,
      white_feather: settings.whiteFeather
    }
  }

  const viewerSettingsPayload = (settings = state.viewerSettings) => ({
    viewer_object_icon_scale: settings.objectIconScale,
    viewer_default_view_mode: settings.defaultViewMode,
    viewer_default_yaw_degrees: settings.defaultYawDegrees,
    viewer_default_pitch_degrees: settings.defaultPitchDegrees,
    viewer_default_zoom_multiplier: settings.defaultZoomMultiplier,
    viewer_default_2d_zoom_multiplier: settings.default2DZoomMultiplier,
    viewer_default_2d_offset_x: settings.default2DOffsetX,
    viewer_default_2d_offset_z: settings.default2DOffsetZ,
    viewer_default_vertical_bias: settings.defaultVerticalBias,
    viewer_walkthrough_offset_x: settings.walkthroughOffsetX,
    viewer_walkthrough_offset_z: settings.walkthroughOffsetZ,
    viewer_walkthrough_yaw_degrees: settings.walkthroughYawDegrees,
    viewer_walkthrough_pitch_degrees: settings.walkthroughPitchDegrees,
    viewer_walkthrough_zoom_multiplier: settings.walkthroughZoomMultiplier,
    viewer_walkthrough_eye_height_ratio: settings.walkthroughEyeHeightRatio,
    viewer_walkthrough_camera_x: settings.walkthroughCameraX,
    viewer_walkthrough_camera_y: settings.walkthroughCameraY,
    viewer_walkthrough_camera_z: settings.walkthroughCameraZ,
    viewer_walkthrough_target_x: settings.walkthroughTargetX,
    viewer_walkthrough_target_y: settings.walkthroughTargetY,
    viewer_walkthrough_target_z: settings.walkthroughTargetZ,
    viewer_model_offset_y: settings.modelOffsetY,
    viewer_labels_visible: settings.labelsVisible,
    viewer_snap_to_ground: settings.snapToGround
  })

  const toggleCablingPlanOverlay = async () => {
    const referenceLayer = activeReferenceLayer()

    if (!referenceLayer) {
      setFeedback("No reference layers are available for this plan yet.", "neutral")
      return false
    }

    return toggleReferenceLayerById(referenceLayer.id)
  }

  const toggleReferenceLayerById = async (referenceLayerId, { showFeedback = true } = {}) => {
    const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)

    if (!referenceLayer) {
      setFeedback("Could not find that reference layer.", "error")
      return false
    }

    if (!referenceLayer.imageUrl || !referenceLayer.processed) {
      if (referenceLayer.processingStatus === "processing") {
        setFeedback(`${referenceLayer.name} is still being generated.`, "neutral")
        renderReferenceLayerMenu()
        renderReferenceLayerList()
        setActiveButton()
        return false
      }

      if (referenceLayer.processingStatus === "failed") {
        setFeedback(referenceLayer.processingError || `${referenceLayer.name} failed to process.`, "error")
        renderReferenceLayerMenu()
        renderReferenceLayerList()
        setActiveButton()
        return false
      }

      setFeedback(`${referenceLayer.name} does not have an image available yet.`, "neutral")
      renderReferenceLayerMenu()
      renderReferenceLayerList()
      setActiveButton()
      return false
    }

    if (state.visibleReferenceLayerIds.has(referenceLayer.id)) {
      state.visibleReferenceLayerIds.delete(referenceLayer.id)
    } else {
      state.visibleReferenceLayerIds.add(referenceLayer.id)
      await ensureCablingPlanOverlay(referenceLayer.id)
    }
    syncActiveReferenceLayerVisibilityState()
    syncCablingPlanOverlayPresentation()
    renderReferenceLayerMenu()
    renderReferenceLayerList()
    setActiveButton()
    renderInspector()
    if (showFeedback) {
      setFeedback(
        state.visibleReferenceLayerIds.has(referenceLayer.id) ? `${referenceLayer.name} is visible.` : `${referenceLayer.name} is hidden.`,
        "neutral"
      )
    }
    return state.visibleReferenceLayerIds.has(referenceLayer.id)
  }

  const applyViewerSettingsToScene = ({ refreshView = true, rebuildPlacements = true } = {}) => {
    syncViewerDataset(state.viewerSettings)
    applyViewerLabelsVisibilityPreference()
    applyModelOffsetToScene()
    grid.position.y = state.viewerSettings.modelOffsetY - 0.01

    if (rebuildPlacements) {
      refreshModelFraming()
      state.referenceLayerOverlaysById.forEach((overlayEntry, referenceLayerId) => {
        const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)
        if (!referenceLayer) return

        const settings = referenceLayerId === state.activeReferenceLayerId
          ? state.cablingPlanSettings
          : cloneCablingPlanSettings(referenceLayer.settings || {})
        applyReferenceLayerSettingsToOverlay(referenceLayer, overlayEntry, settings)
      })
      rebuildPlacementObjects()
      rebuildNavigationPointObjects()
      rebuildCaptureAlignmentObjects()
      refreshSelectionHelper()
      refreshHoverHelper()
    }

    if (refreshView && state.model) {
      if (state.viewMode === "2d") {
        apply2DView()
      } else if (state.viewMode === "walkthrough") {
        applyWalkthroughView()
      } else {
        apply3DView()
      }
    }

    renderInspector()
  }

  const applyViewerSettingChange = (key, rawValue) => {
    const nextViewerSettings = {
      ...state.viewerSettings,
      [key]: rawValue
    }

    if ([
      "walkthroughOffsetX",
      "walkthroughOffsetZ",
      "walkthroughYawDegrees",
      "walkthroughPitchDegrees",
      "walkthroughZoomMultiplier",
      "walkthroughEyeHeightRatio"
    ].includes(key)) {
      nextViewerSettings.walkthroughCameraX = null
      nextViewerSettings.walkthroughCameraY = null
      nextViewerSettings.walkthroughCameraZ = null
      nextViewerSettings.walkthroughTargetX = null
      nextViewerSettings.walkthroughTargetY = null
      nextViewerSettings.walkthroughTargetZ = null
    }

    state.viewerSettings = cloneViewerSettings(nextViewerSettings)
    applyViewerSettingsToScene()
  }

  const sync3DViewerSettingsToCurrentView = () => {
    if (!state.framing) {
      setFeedback("Load the viewer before capturing the default 3D camera.", "neutral")
      return
    }
    if (state.viewMode !== "3d") {
      setFeedback("Switch to 3D mode to capture the default 3D camera.", "neutral")
      return
    }

    const offset = camera.position.clone().sub(controls.target)
    const distance = Math.max(offset.length(), 0.0001)
    const horizontalDistance = Math.hypot(offset.x, offset.z)
    const baseDistance = Math.max(state.framing.distance || 1, 0.0001)
    const yawDegrees = THREE.MathUtils.radToDeg(Math.atan2(offset.z, offset.x))
    const pitchDegrees = THREE.MathUtils.radToDeg(Math.atan2(offset.y, horizontalDistance))

    state.viewerSettings = cloneViewerSettings({
      ...state.viewerSettings,
      defaultYawDegrees: Number(yawDegrees.toFixed(1)),
      defaultPitchDegrees: Number(pitchDegrees.toFixed(1)),
      defaultZoomMultiplier: Number((distance / baseDistance).toFixed(2)),
      defaultVerticalBias: Number(((state.framing.center.y - controls.target.y) / defaultViewVerticalOffsetDistance()).toFixed(2)),
      modelOffsetY: Number(state.viewerSettings.modelOffsetY.toFixed(2)),
      labelsVisible: state.viewerSettings.labelsVisible
    })

    syncViewerDataset(state.viewerSettings)
    syncCablingPlanForm()
    setFeedback("Default 3D camera updated from the current view.", "success")
  }

  const sync2DViewerSettingsToCurrentView = () => {
    if (!state.framing) {
      setFeedback("Load a model before capturing the default 2D camera.", "neutral")
      return
    }
    if (!is2DLikeMode(state.viewMode)) {
      setFeedback("Switch to a 2D view to capture the default 2D camera.", "neutral")
      return
    }

    const nextViewerSettings = {
      ...state.viewerSettings,
      default2DOffsetX: Number((controls.target.x - state.framing.center.x).toFixed(2)),
      default2DOffsetZ: Number((controls.target.z - state.framing.center.z).toFixed(2))
    }

    if (isPerspective2DMode(state.viewMode)) {
      const currentVerticalDistance = camera.position.y - controls.target.y
      const distanceSign = currentVerticalDistance < 0 ? -1 : 1
      const currentSignedDistance = camera.position.distanceTo(controls.target) * distanceSign
      nextViewerSettings.default2DZoomMultiplier = Number(currentSignedDistance.toFixed(3))
    }

    if (isOrthographic2DMode(state.viewMode)) {
      nextViewerSettings.default2DZoomMultiplier = Number((state.ortho2DZoom || 1).toFixed(3))
    }

    state.viewerSettings = cloneViewerSettings(nextViewerSettings)
    syncViewerDataset(state.viewerSettings)
    syncCablingPlanForm()
    setFeedback("Default 2D view updated from the current view.", "success")
  }

  const syncWalkthroughSettingsToCurrentView = () => {
    if (!state.framing) {
      setFeedback("Load the viewer before capturing the walkthrough entry settings.", "neutral")
      return
    }
    if (state.viewMode !== "walkthrough") {
      setFeedback("Switch to walkthrough mode to capture the walkthrough entry settings.", "neutral")
      return
    }

    const derivedSettings = deriveWalkthroughHelperSettings()
    if (!derivedSettings) return

    state.viewerSettings = cloneViewerSettings({
      ...derivedSettings,
      walkthroughCameraX: Number(camera.position.x.toFixed(4)),
      walkthroughCameraY: Number(camera.position.y.toFixed(4)),
      walkthroughCameraZ: Number(camera.position.z.toFixed(4)),
      walkthroughTargetX: Number(controls.target.x.toFixed(4)),
      walkthroughTargetY: Number(controls.target.y.toFixed(4)),
      walkthroughTargetZ: Number(controls.target.z.toFixed(4))
    })

    syncViewerDataset(state.viewerSettings)
    syncCablingPlanForm()
    setFeedback("Walkthrough entry settings updated from the current view.", "success")
  }

  const loadReferenceLayerDraft = (referenceLayer, { expandEditor = true } = {}) => {
    state.activeReferenceLayerId = referenceLayer?.id || null
    if (expandEditor) state.referenceLayerEditorOpenId = referenceLayer?.id || null
    state.cablingPlanName = referenceLayer?.name || ""
    state.cablingPlanSavedName = referenceLayer?.name || ""
    state.cablingPlanDefaultVisible = referenceLayer?.defaultVisible ?? false
    state.cablingPlanSavedDefaultVisible = referenceLayer?.defaultVisible ?? false
    state.cablingPlanSettings = cloneCablingPlanSettings(referenceLayer?.settings || {})
    state.cablingPlanSavedSettings = cloneCablingPlanSettings(referenceLayer?.settings || {})

    syncActiveReferenceLayerVisibilityState()
    syncReferenceLayersDataset()
    renderReferenceLayerMenu()
    renderReferenceLayerList()
    syncCablingPlanForm()
  }

  const upsertReferenceLayer = (payload) => {
    const normalizedReferenceLayer = {
      ...payload,
      settings: cloneCablingPlanSettings(payload?.settings || {})
    }
    const remainingReferenceLayers = state.referenceLayers.filter((referenceLayer) => referenceLayer.id !== normalizedReferenceLayer.id)
    remainingReferenceLayers.push(normalizedReferenceLayer)
    state.referenceLayers = remainingReferenceLayers.sort((left, right) => left.position - right.position)
    markReferenceLayerSaved(normalizedReferenceLayer)
    syncReferenceLayersDataset()
    return normalizedReferenceLayer
  }

  const applyReferenceLayerSelection = async (referenceLayerId, { preserveVisibility = false, showFeedback = false, toggleAccordion = false } = {}) => {
    const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)
    syncActiveReferenceLayerDraftToCollection()

    if (toggleAccordion && state.referenceLayerEditorOpenId === referenceLayerId) {
      state.referenceLayerEditorOpenId = null
      renderReferenceLayerList()
      syncCablingPlanForm()
      renderInspector()
      return
    }

    loadReferenceLayerDraft(referenceLayer, { preserveVisibility, expandEditor: true })
    syncCablingPlanOverlayPresentation()

    if (state.cablingPlanVisible) await ensureCablingPlanOverlay(referenceLayerId)

    setActiveButton()
    renderInspector()

    if (showFeedback && referenceLayer) {
      setFeedback(`Editing ${referenceLayer.name}.`, "success")
    }
  }

  const applyCablingPlanSettingChange = (key, rawValue) => {
    if (key === "name") {
      state.cablingPlanName = rawValue.toString()
      syncActiveReferenceLayerDraftToCollection()
      syncCablingPlanForm()
      renderReferenceLayerMenu()
      return
    }

    if (key === "defaultVisible") {
      state.cablingPlanDefaultVisible = parseBooleanOr(rawValue, false)
      syncActiveReferenceLayerDraftToCollection()
      syncCablingPlanForm()
      return
    }

    const candidateSettings = cloneCablingPlanSettings({
      ...state.cablingPlanSettings,
      [key]: rawValue
    })

    state.cablingPlanSettings = candidateSettings
    syncActiveReferenceLayerDraftToCollection()
    applyCablingPlanSettingsToOverlay()

    if (key === "whiteThreshold" || key === "whiteFeather") {
      scheduleCablingPlanTextureRefresh()
    }

    syncCablingPlanForm()
  }

  const resetViewerSettings = () => {
    state.viewerSettings = cloneViewerSettings(state.savedViewerSettings)
    state.referenceLayers = state.referenceLayers.map((referenceLayer) => {
      const savedReferenceLayer = savedReferenceLayerDraft(referenceLayer.id)
      if (!savedReferenceLayer) return referenceLayer

      return {
        ...referenceLayer,
        name: savedReferenceLayer.name,
        defaultVisible: savedReferenceLayer.defaultVisible,
        settings: cloneCablingPlanSettings(savedReferenceLayer.settings || {})
      }
    })
    loadReferenceLayerDraft(activeReferenceLayer(), { preserveVisibility: true })
    syncViewerDataset(state.viewerSettings)
    applyViewerSettingsToScene({ refreshView: true, rebuildPlacements: true })
    applyCablingPlanSettingsToOverlay()
    scheduleCablingPlanTextureRefresh()
    syncCablingPlanForm()
    setFeedback("Viewer settings restored to the last saved values.", "neutral")
  }

  const saveViewerSettings = async () => {
    if (!element.dataset.viewerSettingsUrl || state.cablingPlanSaving) return

    state.cablingPlanSaving = true
    syncCablingPlanForm()
    setFeedback("Saving settings...", "working")

    try {
      const payload = await apiRequest(element.dataset.viewerSettingsUrl, {
        method: "PATCH",
        body: JSON.stringify({
          viewer_settings: viewerSettingsPayload()
        })
      })

      const savedViewerSettings = cloneViewerSettings(viewerSettingsFromPayload(payload.viewer_settings || {}))
      state.viewerSettings = savedViewerSettings
      state.savedViewerSettings = cloneViewerSettings(savedViewerSettings)
      syncViewerDataset(savedViewerSettings)
      applyViewerSettingsToScene({ refreshView: true, rebuildPlacements: true })
    } catch (error) {
      setFeedback(`Could not save viewer settings: ${error.message}`, "error")
      state.cablingPlanSaving = false
      syncCablingPlanForm()
      return
    }

    try {
      syncActiveReferenceLayerDraftToCollection()
      const dirtyReferenceLayers = state.referenceLayers.filter((referenceLayer) => referenceLayerDirtyFor(referenceLayer))

      for (const referenceLayer of dirtyReferenceLayers) {
        const payload = await apiRequest(referenceLayer.updateUrl, {
          method: "PATCH",
          body: JSON.stringify({
            reference_layer: referenceLayerPayload(referenceLayer)
          })
        })

        const savedReferenceLayer = upsertReferenceLayer(payload.reference_layer || {})
        if (savedReferenceLayer.id === state.activeReferenceLayerId) {
          loadReferenceLayerDraft(savedReferenceLayer, { preserveVisibility: true })
          applyCablingPlanSettingsToOverlay()
          scheduleCablingPlanTextureRefresh()
        }
      }

      setFeedback("Viewer settings saved.", "success", { force: true })
    } catch (error) {
      setFeedback(`Could not save reference layer settings: ${error.message}`, "error")
    } finally {
      state.cablingPlanSaving = false
      syncCablingPlanForm()
    }
  }

  const createReferenceLayer = async () => {
    if (!element.dataset.referenceLayersUrl || state.cablingPlanSaving) return

    const name = referenceLayerCreateNameInput?.value?.toString().trim()
    const file = referenceLayerCreateFileInput?.files?.[0]

    if (!name || !file) {
      setFeedback("Choose a name and a PDF file to add a reference layer.", "error")
      return
    }

    state.cablingPlanSaving = true
    syncCablingPlanForm()
    setFeedback("Adding reference layer...", "working")

    try {
      const formData = new FormData()
      formData.append("reference_layer[name]", name)
      formData.append("reference_layer[pdf]", file)
      formData.append("reference_layer[position]", String(state.referenceLayers.length))

      const payload = await apiRequest(element.dataset.referenceLayersUrl, {
        method: "POST",
        body: formData
      })

      const createdReferenceLayer = upsertReferenceLayer(payload.reference_layer || {})
      if (referenceLayerCreateForm) referenceLayerCreateForm.reset()
      await applyReferenceLayerSelection(createdReferenceLayer.id, { showFeedback: false })
      setFeedback(`${createdReferenceLayer.name} added. PDF processing has started.`, "success")
    } catch (error) {
      setFeedback(`Could not add reference layer: ${error.message}`, "error")
    } finally {
      state.cablingPlanSaving = false
      syncCablingPlanForm()
    }
  }

  const replaceReferenceLayerPdf = async (referenceLayerId = state.activeReferenceLayerId, file = null) => {
    const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)
    const uploadedFile = file || null
    if (referenceLayerId !== state.activeReferenceLayerId) {
      await applyReferenceLayerSelection(referenceLayerId, { preserveVisibility: true })
    }
    if (!referenceLayer || !uploadedFile || state.cablingPlanSaving) return

    state.cablingPlanSaving = true
    syncCablingPlanForm()
    setFeedback(`Uploading replacement PDF for ${referenceLayer.name}...`, "working")

    try {
      const formData = new FormData()
      const referenceLayerData = findReferenceLayerById(state.referenceLayers, referenceLayerId)
      const settings = cloneCablingPlanSettings(referenceLayerData?.settings || {})
      formData.append("reference_layer[name]", referenceLayerData?.name?.toString().trim() || "")
      formData.append("reference_layer[default_visible]", referenceLayerData?.defaultVisible ? "true" : "false")
      formData.append("reference_layer[pdf]", uploadedFile)
      formData.append("reference_layer[offset_x]", String(settings.offsetX))
      formData.append("reference_layer[offset_z]", String(settings.offsetZ))
      formData.append("reference_layer[elevation]", String(settings.elevation))
      formData.append("reference_layer[rotation_degrees]", String(settings.rotationDegrees))
      formData.append("reference_layer[scale]", String(settings.scale))
      formData.append("reference_layer[opacity]", String(settings.opacity))
      formData.append("reference_layer[white_threshold]", String(settings.whiteThreshold))
      formData.append("reference_layer[white_feather]", String(settings.whiteFeather))

      const payload = await apiRequest(referenceLayer.updateUrl, {
        method: "PATCH",
        body: formData
      })

      const savedReferenceLayer = upsertReferenceLayer(payload.reference_layer || {})
      state.visibleReferenceLayerIds.delete(referenceLayer.id)
      syncActiveReferenceLayerVisibilityState()
      disposeCablingPlanOverlay(referenceLayer.id)
      loadReferenceLayerDraft(savedReferenceLayer, { preserveVisibility: true })
      setFeedback(`${savedReferenceLayer.name} replacement PDF uploaded.`, "success")
    } catch (error) {
      setFeedback(`Could not replace the PDF: ${error.message}`, "error")
    } finally {
      state.cablingPlanSaving = false
      syncCablingPlanForm()
    }
  }

  const deleteReferenceLayer = async (referenceLayerId = state.activeReferenceLayerId) => {
    const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)
    if (!referenceLayer || state.cablingPlanSaving) return

    const confirmed = window.confirm(`Delete reference layer "${referenceLayer.name}"?`)
    if (!confirmed) return

    state.cablingPlanSaving = true
    syncCablingPlanForm()
    setFeedback(`Deleting ${referenceLayer.name}...`, "working")

    try {
      await apiRequest(referenceLayer.destroyUrl, { method: "DELETE", body: JSON.stringify({}) })
      state.referenceLayers = state.referenceLayers.filter((entry) => entry.id !== referenceLayer.id)
      state.savedReferenceLayersById.delete(referenceLayer.id)
      state.visibleReferenceLayerIds.delete(referenceLayer.id)
      syncActiveReferenceLayerVisibilityState()
      const nextReferenceLayerId = initialReferenceLayerId(state.referenceLayers)
      disposeCablingPlanOverlay(referenceLayer.id)
      await applyReferenceLayerSelection(nextReferenceLayerId, { preserveVisibility: false })
      setFeedback(`Deleted ${referenceLayer.name}.`, "success")
    } catch (error) {
      setFeedback(`Could not delete reference layer: ${error.message}`, "error")
    } finally {
      state.cablingPlanSaving = false
      syncCablingPlanForm()
    }
  }

  const toggleViewerSettingsPanel = async () => {
    if (state.sidePanelMode === "viewer-settings") {
      state.sidePanelMode = null
      renderInspector()
      setFeedback(
        anyViewerSettingsDirty()
          ? "Viewer settings panel closed. Unsaved preview changes are still visible."
          : "Viewer settings panel closed.",
        "neutral"
      )
      return
    }

    state.sidePanelMode = "viewer-settings"
    state.mappingMode = false
    state.guidedStep = 1
    state.preferAdvancedEditor = false
    clearHoveredObject()
    clearSelectedObject()
    closePopup()
    renderer.domElement.style.cursor = "default"

    if (state.visibleReferenceLayerIds.size > 0) {
      await ensureVisibleReferenceLayerOverlays()
      syncCablingPlanOverlayPresentation()
    } else if (!activeReferenceLayer()?.processed) {
      setFeedback("Viewer settings are open. The selected reference layer does not have an image available for preview yet.", "neutral")
    }

    renderReferenceLayerMenu()
    renderReferenceLayerList()
    renderInspector()
    expandViewerSettingsForCurrentView()
  }

  return {
    toggleCablingPlanOverlay,
    toggleReferenceLayerById,
    applyViewerSettingsToScene,
    applyViewerSettingChange,
    sync3DViewerSettingsToCurrentView,
    sync2DViewerSettingsToCurrentView,
    syncWalkthroughSettingsToCurrentView,
    loadReferenceLayerDraft,
    upsertReferenceLayer,
    applyReferenceLayerSelection,
    applyCablingPlanSettingChange,
    resetViewerSettings,
    saveViewerSettings,
    createReferenceLayer,
    replaceReferenceLayerPdf,
    deleteReferenceLayer,
    toggleViewerSettingsPanel
  }
}
